'use client';

import { useTranslations } from 'next-intl';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetch } from '@/app/hooks';
import { useStoreApi } from '@/app/providers/store';
import type { ServiceError } from '@/app/types';
import { extractMessageText, isYoloLikeMessageList, mergeConsecutiveAssistantMessages } from '../_utils';
import { clawsGetChatHistory, clawsUpdateSessionReadCursor } from '../apis';
import type { ChatMessageItem, ClawMessage, SessionMode } from '../types';
import { getLatestStreamSeqFromHistory, inferRunningTurnId, isAwaitingAssistantReply } from './chat-history-helpers';

const PAGE_SIZE = 100;

type ChatHistoryQuery = NonNullable<NonNullable<Parameters<typeof clawsGetChatHistory>[0]>['query']>;

export interface RunningTurn {
  sessionId: string;
  turnId: string;
  /**
   * 历史最新一条为 user、assistant 尚未落地（例如用户刚回复 go）。
   * 此时 turnId 可能为空，SSE 首帧才会带上新 turn_id；仅用于拉起思考态占位。
   */
  awaitingReply?: boolean;
}

interface UseChatHistoryOptions {
  activeSessionId: string | null;
  shouldBootstrapInitially: boolean;
  shouldSkipHydration: () => boolean;
  getCurrentReadStreamSeq?: () => string | null | undefined;
  setCurrentReadStreamSeq?: (streamSeq: string | null | undefined) => void;
  setMessages: Dispatch<SetStateAction<ChatMessageItem[]>>;
  onHistoryAccessError?: (error: { code: number; reason: string; message: string }) => void;
  /**
   * 当前 session 模式。yolo 模式下后端会下发若干条彼此独立的 assistant 消息
   * （分镜卡 / 视频附件 / "已完成"提示），它们必须在 history 重放时各自渲染，
   * 不能再像 default 模式那样合并到同一条 assistant 气泡里。
   */
  sessionMode?: SessionMode | null;
  /**
   * 后端 session.run_phase，来自 state/stream 或 detail 接口。
   * 仅用于压制误判：history 末尾是 user 消息通常意味着等待回复，
   * 但 run_phase==='idle' 时后端已确认空闲（用户的那条消息未触发新 turn），
   * 此时不应把 awaitingReply 拉起 isStreaming。
   */
  runPhase?: string | null;
}

export const useChatHistory = ({
  activeSessionId,
  shouldBootstrapInitially,
  shouldSkipHydration,
  getCurrentReadStreamSeq,
  setCurrentReadStreamSeq,
  setMessages,
  onHistoryAccessError,
  sessionMode,
  runPhase,
}: UseChatHistoryOptions) => {
  const t = useTranslations();
  const storeApi = useStoreApi();
  const [isHistoryBootstrapping, setIsHistoryBootstrapping] = useState<boolean>(() => shouldBootstrapInitially);
  const [lastStreamSeq, setLastStreamSeq] = useState<string | null | undefined>(undefined);
  // 历史加载完毕后从最新 turn 推断是否仍在跑：用于在 SSE 还没回数据前就让 UI 进入"思考中"。
  // 由 useChatStream 的订阅 effect 消费；session 切换时立即清空，避免跨 session 串状态。
  const [runningTurn, setRunningTurn] = useState<RunningTurn | null>(null);
  const historyRequestIdRef = useRef(0);
  const newSessionIdsRef = useRef<Set<string>>(new Set());
  const prevActiveSessionIdRef = useRef<string | null>(activeSessionId);
  const failedFallbackMessage = t('claw.chat.error.failed');

  const markAsNewSession = useCallback((sessionId: string) => {
    newSessionIdsRef.current.add(sessionId);
  }, []);

  const { runAsync: fetchChatHistory, loading: historyLoading } = useFetch(
    ({ targetSessionId, page, role }: { targetSessionId: string; page: number; role?: ChatHistoryQuery['role'] }) =>
      clawsGetChatHistory({
        query: {
          session_id: targetSessionId,
          'pager.page': page,
          'pager.pagesz': PAGE_SIZE,
          ...(role ? { role } : {}),
        },
      }),
    {
      manual: true,
    },
  );

  const notifyHistoryAccessError = (error?: ServiceError) => {
    if (!error) return false;

    const isForbidden = error.code === 403 || error.reason === 'Forbidden';
    const isSessionNotFound = error.code === 404 || error.reason === 'SessionNotFound';
    if (!isForbidden && !isSessionNotFound) return false;

    onHistoryAccessError?.({
      code: error.code,
      reason: error.reason,
      message: error.message,
    });
    return true;
  };

  const reportedReadSeqRef = useRef<Record<string, string>>({});

  const reportReadCursor = useCallback(async (sessionId: string, streamSeq: string | null | undefined) => {
    if (!sessionId || !streamSeq) return;

    const parsed = Number(streamSeq);
    if (!Number.isFinite(parsed) || parsed <= 0) return;

    const lastReported = reportedReadSeqRef.current[sessionId];
    if (lastReported != null && Number(lastReported) >= parsed) {
      return;
    }

    try {
      await clawsUpdateSessionReadCursor({
        body: {
          session_id: sessionId,
          last_read_stream_seq: String(parsed),
        },
      });
      reportedReadSeqRef.current[sessionId] = String(Math.max(Number(lastReported ?? 0), parsed));
    } catch {
      // ignore read cursor sync failures; unread will self-heal on next successful sync
    }
  }, []);

  useEffect(() => {
    // 区分"真正切换了 session"和"同一 session 内 sessionMode 后到位"两种触发：
    // 后者不能清空已加载的消息，否则会把对话清掉再重灌一次，反而引入新的闪烁。
    const isSessionSwitch = prevActiveSessionIdRef.current !== activeSessionId;
    prevActiveSessionIdRef.current = activeSessionId;

    if (!activeSessionId) {
      historyRequestIdRef.current += 1;
      setIsHistoryBootstrapping(false);
      setLastStreamSeq(undefined);
      setCurrentReadStreamSeq?.(undefined);
      setRunningTurn(null);
      setMessages([]);
      return;
    }

    if (newSessionIdsRef.current.has(activeSessionId)) {
      newSessionIdsRef.current.delete(activeSessionId);
      historyRequestIdRef.current += 1;
      setIsHistoryBootstrapping(false);
      setLastStreamSeq(null);
      setCurrentReadStreamSeq?.(null);
      setRunningTurn(null);
      return;
    }

    const requestId = historyRequestIdRef.current + 1;
    historyRequestIdRef.current = requestId;
    setRunningTurn(null);
    // session 切换瞬间立刻把 seq 置回 undefined，让 SSE 订阅在历史 fetch 期间停摆。
    // 否则上一个 session 的 seq 会被带去订阅新 session（或回切回来的旧 session），
    // 后端会按错误游标全量回放历史事件，把已完结的最后一条 assistant 误置为思考/打字态。
    setLastStreamSeq(undefined);
    setCurrentReadStreamSeq?.(undefined);
    // 真正切换 session 时立即清空消息，保证 isHistoryBootstrapping=true 和 messages=[] 同帧生效：
    // - 避免 loading 结束前残留上一个 session 的消息；
    // - 避免 loading 状态未及时生效时闪一帧 ChatEmptySession。
    // sessionMode 后到位触发的重跑不走这里（isSessionSwitch=false），保留已加载消息由下方 hydration 覆盖。
    if (isSessionSwitch) {
      setMessages([]);
    }
    setIsHistoryBootstrapping(true);

    const loadChatHistory = async () => {
      try {
        const historyRecords: ClawMessage[] = [];
        let page = 0;

        while (true) {
          const result = await fetchChatHistory({
            targetSessionId: activeSessionId,
            page,
          });
          if (notifyHistoryAccessError(result.error as ServiceError | undefined)) return;
          if (result.error || !result.data) return;
          if (historyRequestIdRef.current !== requestId) return;

          const currentPageMessages = result.data.messages ?? [];
          historyRecords.push(...currentPageMessages);

          if (currentPageMessages.length < PAGE_SIZE) {
            break;
          }
          page += 1;
        }

        // 记录最新一条消息的 stream_seq，用于 SSE 断点续传订阅
        // 注意：即便随后因为流式进行中而跳过历史回填，这里也要把 lastStreamSeq 落下来，
        // 否则 events stream 会因为 lastStreamSeq === undefined 永远不起订阅
        const latestHistoryStreamSeq = getLatestStreamSeqFromHistory(historyRecords);
        setLastStreamSeq(latestHistoryStreamSeq);
        setCurrentReadStreamSeq?.(latestHistoryStreamSeq);

        // 推断最近一个 turn 是否仍在进行；若是，提前把 runningTurn 抛给上层，
        // useChatStream 会在 SSE 回数据前就让 UI 进入"思考中"。
        const inferredTurnId = inferRunningTurnId(historyRecords);
        // run_phase==='idle' 时后端已确认空闲，awaitingReply 判定不可信：
        // coordinator session 里用户发了"进度如何"后 session 直接 idle，
        // 没有新 turn 产生，不应把 isStreaming 拉起。
        const awaitingReply = runPhase !== 'idle' && isAwaitingAssistantReply(historyRecords);
        setRunningTurn(
          inferredTurnId || awaitingReply
            ? {
                sessionId: activeSessionId,
                turnId: inferredTurnId ?? '',
                awaitingReply: awaitingReply && !inferredTurnId,
              }
            : null,
        );

        // 流式对话进行中时，避免历史回填覆盖本地 user/assistant 临时消息
        if (shouldSkipHydration()) {
          return;
        }

        const conversationMessages = [...historyRecords]
          .reverse()
          .map((message) => extractMessageText(message, { failedFallbackMessage }))
          .filter((message): message is ChatMessageItem => message !== null);
        // yolo 会话：history 中分镜卡 / 视频附件 / 完成提示是若干条独立的 assistant
        // 消息，必须各自渲染，不能再像 default 模式那样合并到同一条气泡里。
        // 既看 sessionMode，也按消息体里是否出现 story 兜底（mode 同步可能滞后）。
        // cron/spawn 消息已并入对话流：作为独立消息按时间顺序渲染，
        // 且会打断前后 assistant 的合并（mergeConsecutiveAssistantMessages 把非 assistant 角色视作边界）。
        const isYoloHistory = sessionMode === 'yolo' || isYoloLikeMessageList(conversationMessages);
        setMessages(isYoloHistory ? conversationMessages : mergeConsecutiveAssistantMessages(conversationMessages));
        storeApi.getState().clearSessionUnread(activeSessionId);
        void reportReadCursor(activeSessionId, getCurrentReadStreamSeq?.() ?? latestHistoryStreamSeq);
      } finally {
        if (historyRequestIdRef.current === requestId) {
          setIsHistoryBootstrapping(false);
        }
      }
    };

    loadChatHistory();
    // sessionMode 进入依赖：session 列表后到位时 mode 会从 undefined 变 'yolo'，
    // hydration 需要按新模式重新走一遍合并逻辑（yolo 不合并 / default 合并）。
    // 进行中的流式态由 shouldSkipHydration() 兜住，不会被覆盖。
  }, [activeSessionId, failedFallbackMessage, sessionMode, getCurrentReadStreamSeq, setCurrentReadStreamSeq]);

  useEffect(() => {
    return () => {
      if (!activeSessionId) return;
      void reportReadCursor(activeSessionId, getCurrentReadStreamSeq?.() ?? lastStreamSeq);
    };
  }, [activeSessionId, getCurrentReadStreamSeq, lastStreamSeq, reportReadCursor]);

  useEffect(() => {
    const flushActiveSessionReadCursor = () => {
      if (!activeSessionId) return;
      void reportReadCursor(activeSessionId, getCurrentReadStreamSeq?.() ?? lastStreamSeq);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        flushActiveSessionReadCursor();
      }
    };

    window.addEventListener('pagehide', flushActiveSessionReadCursor);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pagehide', flushActiveSessionReadCursor);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [activeSessionId, getCurrentReadStreamSeq, lastStreamSeq, reportReadCursor]);

  return {
    isHistoryBootstrapping,
    historyLoading,
    lastStreamSeq,
    runningTurn,
    markAsNewSession,
  };
};
